/**
 * `Entries that need review`: the candidate filter and the AJAX layer over the card.
 *
 * That layer swaps the whole card for everything but one request. Every mutation on this screen changes
 * more than the entry it was taken on: matching one entry moves another category's counter, grows the
 * selection and clears the status rules through `clearReviewRules()`. Reassembling those dependencies in
 * the browser is how a screen ends up showing numbers from two different moments side by side, which is
 * the defect this whole round started from. So the server answers with the screen it wants shown and
 * this module paints it.
 *
 * The catalogue search is the exception, and it is one because of what it changes rather than because a
 * narrow swap is nicer: it answers for one entry and for nothing else - no counter, no tab, no page, no
 * other entry moves with it. So its answer is still the whole screen, but one `<li>` is lifted out of it
 * and only that entry is replaced, which leaves the rest of the card alive while a provider request is
 * on the wire. The server is told (`X-Steam-Review-Scope: entry`), because a render nobody sees whole
 * may not consume the one-shot bulk-dismissal banner.
 *
 * Nothing here is required for the screen to work. Every action is an ordinary form or link; without
 * JavaScript - or when a request fails - the same submit goes out natively and the server's own answer,
 * complete with the `#steam-review-entry-N` fragment, is a better reply than a message invented here.
 */
import { announcePaginationPosition } from '../components/pagination-feedback.js';
import { canAnimateElement, prefersReducedMotion } from '../components/motion-policy.js';

const CARD_SELECTOR = '[data-steam-review-card]';

/**
 * How long a request may hang before the card is given back.
 *
 * Without it a connection that never answers leaves the card `aria-busy` and inert for good, because
 * the `finally` that clears it never runs. Long enough to cover a slow catalogue search, which is the
 * one request here that waits on a third party.
 */
const REQUEST_TIMEOUT_MS = 15_000;

/**
 * How long the row takes to reach its new height. Matched by the entering animation in
 * `pages/steam-import.css`, so the height and what fills it settle together rather than one after the
 * other.
 */
const ENTRY_SWAP_DURATION_MS = 220;

/** Left on the entry that has just arrived; the stylesheet fades its contents in from it. */
const ENTERING_CLASS = 'is-entering';

/**
 * How many of a collection's matched titles an answered entry shows before the toggle.
 *
 * Fixed, and deliberately not "unless only one would be hidden": `Show 1 more` for a single click reads
 * oddly, but a conditional threshold would make the height of a compact card depend on how many games
 * are behind it - and the same card would then be two different shapes at four titles and at five.
 */
const MATCHED_TITLE_PREVIEW = 3;

export function initSteamImportReview(
    documentRef = globalThis.document,
    windowRef = globalThis.window,
    fetchImpl = globalThis.fetch?.bind(globalThis),
    timerApi = globalThis,
) {
    if (
        typeof documentRef?.addEventListener !== 'function'
        || documentRef.querySelector('[data-steam-review]') === null
    ) {
        return;
    }

    // Delegated, so the entries a swap brings in need no re-binding.
    documentRef.addEventListener('input', (event) => {
        if (event.target instanceof HTMLInputElement) {
            syncSearchField(event.target);
        }
    });

    // The clear button the shared search field renders, on both boxes of this screen. Emptying the box
    // by hand and pressing this are the same thing, so they run the same code.
    documentRef.addEventListener('click', (event) => {
        const button = event.target?.closest?.('[data-steam-review-clear]');
        if (!(button instanceof HTMLElement)) {
            return;
        }

        const field = fieldOf(button);
        if (!(field instanceof HTMLInputElement)) {
            return;
        }

        field.value = '';
        field.focus?.();
        syncSearchField(field);
    });

    // `Collections` only, because it is the only category whose answer is a set. Delegated for the same
    // reason as the filter: the entries a card swap brings in are new elements.
    documentRef.addEventListener('change', (event) => {
        const form = event.target?.closest?.('[data-steam-review-candidates]');
        if (!(form instanceof HTMLElement)) {
            return;
        }

        updateUseSelectedLabel(form);
    });

    /**
     * The bulk selection of a large collection, and the way out of an edit.
     *
     * All three operate checkboxes that are already on the page, so all three are finished in the
     * browser and none of them is required: the list can be answered one box at a time without any of
     * this. `Select all` takes what the filter is currently showing, which is what its own label says it
     * will take - anything else and a filtered list would silently pick up the fifty games it is hiding.
     */
    documentRef.addEventListener('click', (event) => {
        const selectAll = event.target?.closest?.('[data-steam-review-select-all]');
        const clearSelection = event.target?.closest?.('[data-steam-review-clear-selection]');
        const cancelEdit = event.target?.closest?.('[data-steam-review-cancel-edit]');
        const trigger = selectAll ?? clearSelection ?? cancelEdit;
        if (!(trigger instanceof HTMLElement)) {
            return;
        }

        const form = trigger.closest?.('[data-steam-review-candidates]');
        if (!(form instanceof HTMLElement)) {
            return;
        }

        if (selectAll instanceof HTMLElement) {
            for (const box of shownCandidateBoxes(form)) {
                box.checked = true;
            }
        } else if (clearSelection instanceof HTMLElement) {
            for (const box of form.querySelectorAll?.('input[type="checkbox"]') ?? []) {
                box.checked = false;
            }
        } else {
            // A native reset, run here rather than left to the button's own default action: the default
            // happens after this event is dispatched, so a label rendered from the listener would
            // describe the selection being discarded. Done by hand, everything below is in step - and
            // the disclosure closes, because leaving the edit is the whole point of the press.
            event.preventDefault?.();
            form.reset?.();
            // The filter box is reset with everything else, but what it hid is hidden in the DOM and no
            // reset touches that: without this the entry reopens with an empty filter over a list still
            // missing rows, and `Select all` would then take that subset while naming the whole
            // collection.
            const filter = form.querySelector?.('[data-steam-review-candidate-filter]');
            if (filter instanceof HTMLInputElement) {
                syncSearchField(filter);
            }

            trigger.closest?.('details')?.removeAttribute?.('open');
        }

        updateUseSelectedLabel(form);
    });

    // Every answered entry the server rendered, collapsed to its preview. The markup arrives with the
    // whole list visible and the toggle hidden, so an entry is readable in full without this module and
    // nothing is locked behind a control that would not work.
    collapseMatchedTitles(documentRef);

    documentRef.addEventListener('click', (event) => {
        const toggle = event.target?.closest?.('[data-steam-review-titles-toggle]');
        if (!(toggle instanceof HTMLElement)) {
            return;
        }

        const group = toggle.closest?.('[data-steam-review-titles]');
        if (group instanceof HTMLElement) {
            renderMatchedTitles(group, toggle.getAttribute('aria-expanded') !== 'true');
        }
    });

    if (typeof fetchImpl !== 'function') {
        return;
    }

    /**
     * The request that owns the card, if any.
     *
     * Three rules, and the asymmetry between them is the point.
     *
     * A **mutation** is refused only while another mutation is in flight. That is the second press of
     * `Use this game` before the first was decided, and neither queueing it nor letting it fall through
     * to a native POST is an answer.
     *
     * A **read** supersedes a read - a tab, a page, a step back each describe the screen being asked for
     * now, and the older answer must not paint over the newer one - and it calls off every catalogue
     * search with it, because the card those answers belong to is the one about to be replaced.
     *
     * A **read waits for a mutation** rather than aborting it, and that one is not about the screen.
     * Aborting a POST only makes this browser stop listening; the request is already with the server and
     * its transaction can still commit. A read that cut in would render the state from before a write
     * that has already happened - the entry still unanswered, the counters unmoved, the status rules
     * cleared underneath it - which is the exact defect this screen was rebuilt to remove.
     */
    let inFlight = null;

    /**
     * The catalogue searches in flight, one slot per entry.
     *
     * Two entries are two questions, and neither is an answer to the other: a search started at
     * `DiRT 3` is no reason to stop waiting for the one started at `The Ship Tutorial`, so they run
     * side by side and each repaints its own entry. Only a second search *on the same entry* supersedes
     * the first, and the slot is what says which of the two still owns the entry it is dimming.
     *
     * @type {Map<string, object>}
     */
    const searches = new Map();

    /**
     * Bumped on every step through history.
     *
     * A mutation's answer carries the URL the server redirected to, and writing it into the address bar
     * is right only while the address bar is still the one the decision was taken on. A step back taken
     * while the decision is in flight moves the browser to another entry, and stamping the mutation's
     * URL onto *that* entry leaves it naming a screen nobody is looking at - with the read that follows
     * rendering the entry it actually belongs to. So the write is skipped when this has moved.
     */
    let historySteps = 0;

    const load = async (url, { form = null, submitter = null, entryAppId = null } = {}) => {
        const isMutation = form !== null;
        // Only a decision waiting on a decision is refused; a `GET` in flight is called off instead,
        // since nothing of a read is with the server and a press that vanished without a trace is not
        // an answer either.
        if (entryAppId === null && isMutation && inFlight?.isMutation === true) {
            return { status: 'busy' };
        }

        // A decision is on the wire and its answer is the whole card. Anything rendering before it has
        // landed would show the screen from before a write that has already happened.
        while (inFlight !== null && inFlight.isMutation) {
            await inFlight.settled;
        }

        if (entryAppId === null) {
            inFlight?.controller.abort();
            // Every entry of the card this is about to replace goes with it, so the searches still
            // waiting for those entries have nothing left to paint into.
            for (const search of searches.values()) {
                search.controller.abort();
            }
        } else {
            // This entry's own search and no other: the rest of the screen is not asking this question.
            searches.get(entryAppId)?.controller.abort();
        }

        // A catalogue search answers for one entry and for nothing else on the screen, so it is the one
        // request that neither blocks nor repaints the rest of the card. Everything else owns the card.
        const target = entryAppId === null
            ? documentRef.querySelector(CARD_SELECTOR)
            : documentRef.querySelector(entrySelector(entryAppId));
        if (!(target instanceof HTMLElement)) {
            return { status: 'failed' };
        }

        const controller = new AbortController();
        let markSettled = () => {};
        const settled = new Promise((resolve) => {
            markSettled = resolve;
        });
        const request = { controller, timedOut: false, isMutation, settled };
        const timer = timerApi.setTimeout(() => {
            request.timedOut = true;
            controller.abort();
        }, REQUEST_TIMEOUT_MS);
        if (entryAppId === null) {
            inFlight = request;
        } else {
            searches.set(entryAppId, request);
        }

        target.setAttribute('aria-busy', 'true');

        try {
            const response = await fetchImpl(
                url,
                requestOptions(form, controller.signal, submitter, entryAppId),
            );
            if (response?.ok !== true) {
                return { status: 'failed' };
            }

            const html = await response.text();
            if (entryAppId !== null) {
                // The whole `<li>`, never its `innerHTML`: the entry's own attributes are part of the
                // answer - `data-decision` is what the stylesheet reads, and the `id` is what the
                // server's `#steam-review-entry-N` fragment points at.
                const entry = extractElement(documentRef, html, entrySelector(entryAppId));
                if (entry === null) {
                    return { status: 'failed' };
                }

                swapEntry(windowRef, target, entry);
                // The field that sent this request was inside the element just removed, so focus was
                // lost with it. It goes back to the same field on the entry that replaced it.
                entry.querySelector?.('[data-steam-review-search] input')?.focus?.();

                return { status: 'ok', responseUrl: null };
            }

            const markup = extractCardMarkup(documentRef, html);
            if (markup === null) {
                return { status: 'failed' };
            }

            const responseUrl = typeof response.url === 'string' && response.url !== '' ? response.url : null;
            swapCard(documentRef, windowRef, target, markup, responseUrl);

            return {
                status: 'ok',
                // Where the server ended up, which after a mutation is the redirect's own URL - the
                // category, the page and the entry's fragment, and never the search that was open when
                // the decision was taken.
                responseUrl,
            };
        } catch (_error) {
            // A dropped connection, a timeout, a request called off by a newer one: this module no
            // longer knows the state of the screen, and each of the three is recovered differently by
            // the caller.
            if (request.timedOut) {
                return { status: 'timeout' };
            }

            return { status: controller.signal.aborted ? 'superseded' : 'failed' };
        } finally {
            timerApi.clearTimeout(timer);
            // Only the request that still owns what it dimmed gives it back. A superseded one would
            // otherwise wake the card - or the entry - up underneath the request that replaced it; a
            // called-off one that skipped this would leave its entry dimmed and unsearchable for good.
            if (entryAppId === null) {
                if (inFlight === request) {
                    inFlight = null;
                    target.removeAttribute('aria-busy');
                }
            } else if (searches.get(entryAppId) === request) {
                searches.delete(entryAppId);
                target.removeAttribute('aria-busy');
            }

            // Released after the slot is free, so a read waiting on this one finds nothing in flight.
            markSettled();
        }
    };

    // Capture, so this runs before the shared pager's own delegated click and the `preventDefault()`
    // below is what takes the page step off it. The pager stays the thing that *renders* the page links
    // and their URLs; what it no longer does on this screen is fetch, swap and record history, because
    // a tab and a page step are the same card changing and two modules answering that is how one of them
    // paints a window into a card the other has already replaced.
    documentRef.addEventListener('click', async (event) => {
        if (isModifiedClick(event)) {
            return;
        }

        const tab = event.target?.closest?.('[data-steam-review-tab]');
        const pageLink = event.target?.closest?.('[data-page-number]');
        const link = tab ?? pageLink;
        const href = link?.getAttribute?.('href');
        if (!(link instanceof HTMLElement) || typeof href !== 'string' || href === '') {
            return;
        }

        event.preventDefault?.();
        // Which list the click belongs to, taken before the swap detaches the element it was in. A tab
        // is not one of them: it moves both.
        const movedList = pageLink?.closest?.('[data-pagination-page-label]');
        const movedPageParam = link === pageLink
            ? movedList?.dataset?.paginationPageParam ?? 'page'
            : null;
        const result = await load(href);

        // The URL changes with the category and with the page, or the browser's back button leaves the
        // screen instead of returning to where it was - and a deep link to `?bucket=ambiguous` has to
        // open it.
        if (result.status === 'ok') {
            windowRef?.history?.pushState?.({ steamReviewCard: true }, '', href);
            announcePage(documentRef, movedPageParam);

            return;
        }

        if (result.status !== 'superseded') {
            windowRef?.location?.assign?.(href);
        }
    }, { capture: true });

    documentRef.addEventListener('submit', async (event) => {
        // The two kinds of form this module answers for, looked up separately so which one it is comes
        // out of the lookup rather than out of a second test on the result. Everything else on the
        // screen - the bulk dismissal, anything a shared component renders - submits natively.
        const search = event.target?.closest?.('[data-steam-review-search]');
        const mutation = event.target?.closest?.('[data-steam-review-mutation]');
        const form = search ?? mutation;
        if (!(form instanceof HTMLElement)) {
            return;
        }

        event.preventDefault?.();

        if (form === search) {
            // A search is a read of this screen with `search_app_id` and `sq` on it, so its own URL is
            // the request. Pushed like a category, which is what makes a reopened search reproducible.
            const url = searchUrl(form);
            if (url === null) {
                form.submit?.();

                return;
            }

            // Scoped to the entry the box belongs to: a search changes the results under one entry and
            // nothing else on the screen - no counter, no tab, no page, no other entry's open filter.
            const result = await load(url, { entryAppId: searchAppIdOf(form) });
            if (result.status === 'ok') {
                windowRef?.history?.pushState?.({ steamReviewCard: true }, '', url);
            } else if (result.status !== 'superseded') {
                windowRef?.location?.assign?.(url);
            }

            return;
        }

        const historyAtStart = historySteps;
        const result = await load(form.getAttribute('action') ?? '', {
            form,
            submitter: event.submitter ?? null,
        });
        const historyMoved = historySteps !== historyAtStart;

        if (result.status === 'ok') {
            // The screen the answer describes is no longer the one the address bar names: a decision
            // taken from an open search lands on a compact entry, while `search_app_id` and `sq` left
            // in the URL would spend a provider request on reload for a search nothing shows.
            //
            // Unless the browser left in the meantime. The entry it is on now belongs to the read that
            // is about to render it, not to this decision.
            if (result.responseUrl !== null && !historyMoved) {
                windowRef?.history?.replaceState?.(windowRef?.history?.state ?? null, '', result.responseUrl);
            }

            return;
        }

        // Dropped: the entry it names is already being decided, and a second answer for it is not
        // something to send twice.
        if (result.status === 'busy' || result.status === 'superseded') {
            return;
        }

        // Every recovery below takes over the navigation, and the owner has already navigated: a step
        // back was taken while this decision was on the wire, and the read of where they went is queued
        // behind it. That read is the authoritative answer to both failures - it renders whatever the
        // server now holds, write landed or not - so recovering here would send the browser back to the
        // screen the owner just left, on a second POST nobody asked for.
        if (historyMoved) {
            return;
        }

        if (result.status === 'timeout') {
            // The write may or may not have landed, so the screen is re-read rather than the decision
            // re-sent. The server is the only thing that knows which of the two happened.
            windowRef?.location?.reload?.();

            return;
        }

        form.submit?.();
    });

    // The pager hands history over (`data-pagination-ajax-history-owner="custom"`), so a step back is
    // answered once, here, and with a read of the whole card.
    windowRef?.addEventListener?.('popstate', async () => {
        // Counted before anything is awaited, so a decision still in flight sees the move and leaves
        // this history entry's URL alone.
        historySteps += 1;

        const href = windowRef?.location?.href;
        if (typeof href !== 'string' || href === '') {
            return;
        }

        const result = await load(href);
        if (result.status === 'ok') {
            // The live region came back empty with the card, so a step from page 3 to page 2 would
            // otherwise be a silent one. Both lists, because a history entry describes both.
            announcePage(documentRef);

            return;
        }

        if (result.status === 'superseded') {
            return;
        }

        // The browser has already moved the address bar and this module could not follow it. Leaving it
        // there would put a card describing one category under a URL naming another, and the hidden
        // fields of every form on it would then carry the old view back to the server. A plain
        // navigation to where the address bar already points is the only honest answer.
        windowRef?.location?.reload?.();
    });
}

/**
 * The form one search box belongs to: the catalogue search of an entry, or its candidate list.
 *
 * The scope of every lookup around these fields, because both live on the same card once for every
 * entry on the page - a filter's clear button reaching into another entry's search is the only way this
 * can go wrong, and the form is what rules it out.
 */
function searchScopeOf(element) {
    return element.closest?.('[data-steam-review-search]')
        ?? element.closest?.('[data-steam-review-candidates]')
        ?? null;
}

function fieldOf(button) {
    return searchScopeOf(button)?.querySelector?.('input[type="search"]') ?? null;
}

/**
 * What a search box on this screen does with its own value: the clear button follows it, and a
 * candidate filter narrows the list under it.
 */
function syncSearchField(field) {
    const clear = searchScopeOf(field)?.querySelector?.('[data-steam-review-clear]');
    if (clear instanceof HTMLElement) {
        clear.hidden = field.value === '';
    }

    if (field.dataset?.steamReviewCandidateFilter === undefined) {
        return;
    }

    // Scoped to the entry's own form, so one collection's filter cannot hide another's candidates.
    const form = field.closest('[data-steam-review-candidates]');
    const list = form?.querySelector?.('[data-steam-review-candidate-list]');
    if (list instanceof HTMLElement) {
        applyCandidateFilter(list, field.value);
    }

    // `Select all` names what it will actually take, and the filter is what decides that.
    if (form instanceof HTMLElement) {
        updateUseSelectedLabel(form);
    }
}

function requestOptions(form, signal, submitter = null, entryAppId = null) {
    const headers = { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' };
    // Said out loud, because the server has one-shot state to withhold: the bulk-dismissal banner is
    // read and cleared as it is rendered, and an answer only one entry is taken out of would swallow it
    // unseen. `search_app_id` alone cannot stand for this - a step back onto an open search is a full
    // card read carrying the same parameter, and the page without JavaScript carries it too.
    if (entryAppId !== null) {
        headers['X-Steam-Review-Scope'] = 'entry';
    }

    if (form === null) {
        return { headers, signal };
    }

    // The form's own fields, CSRF token included, so the request is the one the no-JavaScript path
    // sends. The redirect it answers with is followed, which is how the re-rendered screen arrives.
    //
    // The submitter is part of that: on a 1:1 entry the picked game rides on the button's own
    // `igdb_source_keys[]`, and a `FormData` built without it posts a decision naming nothing.
    return {
        method: 'POST',
        headers,
        signal,
        credentials: 'same-origin',
        body: new FormData(form, submitter),
    };
}

function searchUrl(form) {
    const action = form.getAttribute('action') ?? '';
    const fields = form.querySelectorAll?.('input') ?? [];
    const params = [];

    for (const field of fields) {
        const name = field?.getAttribute?.('name') ?? field?.name ?? '';
        if (name === '' || name === 'csrf_token') {
            continue;
        }

        params.push(`${encodeURIComponent(name)}=${encodeURIComponent(String(field.value ?? ''))}`);
    }

    if (action === '') {
        return null;
    }

    return params.length === 0 ? action : `${action}?${params.join('&')}`;
}

/**
 * The card replaced by the answer, and what a decision does to the lists on it made visible.
 *
 * A match takes an entry out of the queue and puts it in `Reviewed`; an undo brings it back; a
 * dismissal can empty `Reviewed` entirely. All of it arrives as one new card, so nothing on the old
 * one is left to animate - which is what a view transition is for: the browser holds both states,
 * pairs the entries by the name given here and animates each one from where it was to where it is,
 * fading out the ones that left and in the ones that arrived. The lists resize with them.
 *
 * Only here. The catalogue search is animated per element instead ({@see swapEntry}), because a view
 * transition is one document-wide swap at a time and two entries can be waiting on the provider at
 * once. Card swaps have always been one at a time - that is what `inFlight` is.
 *
 * Without the API, or for a reader who asked for less motion, the card is simply swapped and grown
 * into its new height, with the entry the decision answered fading its own contents in.
 */
function swapCard(documentRef, windowRef, card, markup, responseUrl) {
    const commit = () => {
        // Taken before the card is replaced, while the entries still holding them are in the document.
        const openSearches = collectOpenSearches(documentRef, responseUrl);
        card.innerHTML = markup;
        restoreOpenSearches(documentRef, openSearches);
        // The answer carries whole title lists again, so the count on every toggle is recomputed from
        // what was actually rendered. Without this a `Save changes` that dropped three of six games
        // would leave a card showing three titles under a `Show 3 more` leading nowhere.
        collapseMatchedTitles(documentRef);
    };

    if (typeof documentRef.startViewTransition !== 'function' || prefersReducedMotion(windowRef)) {
        const previousHeight = elementHeight(card);
        commit();
        markEnteringEntry(documentRef, responseUrl);
        animateHeight(windowRef, card, previousHeight);

        return;
    }

    nameEntriesForTransition(documentRef);
    documentRef.startViewTransition(() => {
        commit();
        // The names again, on the entries the answer brought: the callback runs between the two
        // captures, so this is what the second one sees.
        nameEntriesForTransition(documentRef);
    });
}

/**
 * The entries whose catalogue search is open, kept across a card swap.
 *
 * A decision is answered with the whole screen, and one request may name one `search_app_id` - so the
 * server closes every other entry's search simply by not being asked about it, and rerunning them
 * would spend a provider request each. The results are already in the browser, on entries the answer
 * has nothing new to say about, so those elements are moved into the new card rather than replaced.
 *
 * Bounded to entries that are still waiting in the answer, and never the one the decision was about -
 * that one changed shape and its answer is the server's. What a kept element carries from before is
 * its return query (`bucket`, `page`, `rpage`), which decides only where its *next* decision lands;
 * an entry still waiting on the card that has just been rendered is on that same view.
 *
 * @return {Map<string, Element>}
 */
function collectOpenSearches(documentRef, responseUrl) {
    const answered = String(responseUrl ?? '').split('#')[1] ?? '';
    const open = new Map();
    const entries = documentRef.querySelectorAll?.('[data-steam-review-search-open]') ?? [];

    for (const entry of entries) {
        const appId = entry.dataset?.steamAppId;
        if (typeof appId === 'string' && appId !== '' && entry.id !== answered) {
            open.set(appId, entry);
        }
    }

    return open;
}

function restoreOpenSearches(documentRef, openSearches) {
    for (const [appId, entry] of openSearches) {
        const fresh = documentRef.querySelector(entrySelector(appId));
        if (fresh instanceof HTMLElement && fresh.dataset?.decision === 'waiting') {
            fresh.replaceWith(entry);
        }
    }
}

/**
 * Every entry on the card under its own transition name, which is what lets the browser tell that the
 * `DiRT 3` in the answer is the `DiRT 3` that was on screen a moment ago rather than a new row in its
 * place. Keyed by appid - the one thing about an entry that is the same in both lists, and what makes
 * a match read as the entry moving from the queue into `Reviewed`.
 */
function nameEntriesForTransition(documentRef) {
    const entries = documentRef.querySelectorAll?.('[data-steam-review-entry]') ?? [];

    for (const entry of entries) {
        const appId = entry.dataset?.steamAppId;
        if (typeof appId === 'string' && appId !== '') {
            // The decision is part of the name deliberately. An entry that merely moved - the rows
            // under a matched one closing the gap, a page step - keeps its name and slides to where it
            // now is. An entry that was *answered* gets a new one, so it fades out of the queue and in
            // under `Reviewed` instead of flying the length of the screen between two lists that can
            // be a scroll apart.
            const decision = entry.dataset?.decision ?? 'waiting';
            entry.style?.setProperty?.('view-transition-name', `steam-review-entry-${decision}-${appId}`);
        }
    }
}

/**
 * The entry that arrived taking the place of the one that asked.
 *
 * Animated because the change is a change of size as much as of content: an entry that answers with ten
 * results, or with `No catalogue game matches that title`, moves everything under it, and a row that
 * jumps to its new height reads as the page having been replaced rather than as this entry having
 * answered. The height is driven here - there is no CSS transition between two elements - while what
 * the answer brought fades in from the stylesheet, over the same span.
 *
 * Deliberately not a view transition, which the paged-search surface uses: that API animates one
 * document-wide swap at a time, and two entries can be waiting on the provider at once here, so the
 * second would skip the first's. Per element, the rows stay independent.
 */
function swapEntry(windowRef, outgoing, incoming) {
    const previousHeight = elementHeight(outgoing);
    outgoing.replaceWith(incoming);
    collapseMatchedTitles(incoming);
    // Set unconditionally: what the class turns on is opted out of in the stylesheet, the way every
    // other motion on this screen is.
    incoming.classList?.add?.(ENTERING_CLASS);
    animateHeight(windowRef, incoming, previousHeight);
}

/**
 * One element from the height it had to the height it now has.
 *
 * Driven from here rather than by a CSS transition, because in both cases the element's contents were
 * replaced rather than restyled and there is nothing for a transition to start from.
 */
function animateHeight(windowRef, element, previousHeight) {
    const nextHeight = elementHeight(element);
    if (!canAnimateElement(element, windowRef)
        || previousHeight === 0
        || nextHeight === 0
        || previousHeight === nextHeight) {
        return;
    }

    // Clipped only while the box is a size its contents are not, and given back however the animation
    // ends: left on, it would cut the focus ring off any control near the edge.
    element.style?.setProperty?.('overflow', 'clip');
    const release = () => element.style?.removeProperty?.('overflow');
    element
        .animate(
            [{ height: `${previousHeight}px` }, { height: `${nextHeight}px` }],
            { duration: ENTRY_SWAP_DURATION_MS, easing: 'ease' },
        )
        .finished
        .then(release, release);
}

/**
 * The entry a decision was about, which the server names in the fragment of the URL it redirected to -
 * `#steam-review-entry-N`. The whole card came back, but only that one entry answered.
 */
function markEnteringEntry(documentRef, responseUrl) {
    const fragment = String(responseUrl ?? '').split('#')[1] ?? '';
    if (fragment === '') {
        return;
    }

    documentRef.getElementById?.(fragment)?.classList?.add?.(ENTERING_CLASS);
}

function elementHeight(element) {
    const height = element?.getBoundingClientRect?.().height;

    return Number.isFinite(height) && height > 0 ? Math.ceil(height) : 0;
}

const entrySelector = (appId) => `[data-steam-app-id="${appId}"]`;

/** Which entry a catalogue search box belongs to, or null when the markup cannot say. */
function searchAppIdOf(form) {
    const appId = form.closest?.('[data-steam-review-entry]')?.dataset?.steamAppId;

    return typeof appId === 'string' && appId !== '' ? appId : null;
}

/**
 * One element out of a whole rendered page, kept as an element rather than as its markup: what the
 * caller replaces is the node, attributes included.
 */
export function extractElement(documentRef, html, selector) {
    const template = documentRef.createElement?.('template');
    if (template?.content?.querySelector === undefined) {
        return null;
    }

    template.innerHTML = String(html ?? '').trim();

    return template.content.querySelector(selector) ?? null;
}

/**
 * The card out of a whole rendered page, the same way the shared pager takes its results out of one.
 * Null when the document cannot be parsed here at all, which is the caller's signal to let the browser
 * do the navigation itself.
 */
export function extractCardMarkup(documentRef, html) {
    const template = documentRef.createElement?.('template');
    if (template?.content?.querySelector === undefined) {
        return null;
    }

    template.innerHTML = String(html ?? '').trim();
    const incoming = template.content.querySelector(CARD_SELECTOR);

    return incoming === null || incoming === undefined ? null : incoming.innerHTML;
}

/**
 * Where the reader has landed, in the shared pager's own words and in the live region of the list it
 * belongs to.
 *
 * Two lists are paginated on this screen - the queue and `Reviewed` - and both come back with every
 * swap, so announcing the first one found meant a step through `Reviewed` was read out as the queue's
 * unchanged position. `pageParam` names the list that moved, read off the pager that was clicked; null
 * is "both", which is what a tab switch and a step through history are - each of those puts both lists
 * somewhere new, and each region then states its own.
 *
 * Read off the `data-pagination-*` attributes the server sends back with the card, so it says what was
 * rendered rather than what was asked for.
 */
function announcePage(documentRef, pageParam = null) {
    const lists = documentRef.querySelectorAll?.('[data-pagination-page-label]') ?? [];

    for (const list of lists) {
        const listParam = list.dataset?.paginationPageParam ?? 'page';
        if (pageParam !== null && listParam !== pageParam) {
            continue;
        }

        // The scope is the list itself, so the sentence lands in its own pager's region.
        announcePaginationPosition([list], {
            pageLabel: list.dataset?.paginationPageLabel,
            currentPage: list.dataset?.paginationCurrentPage,
            totalPages: list.dataset?.paginationTotalPages,
        });
    }
}

/**
 * Everything on a `Collections` entry that renders the current selection, painted in one pass.
 *
 * The toolbar's `N selected` and the number in the commit button's label are two renderings of one
 * state, not two controls - so they are written together with the button's disabled flag, and there is
 * no path through this module that moves one without the other. The count belongs to the checkboxes, so
 * it is maintained here rather than asked of the server; it is only ever a label. What is saved is what
 * the form posts, and the answer the screen is repainted from is the server's.
 *
 * In an edit (`data-steam-review-static-label`) the button keeps the label the server gave it. Those
 * games are already in use, so the press is `Save changes` whatever the count is - only whether it is
 * pressable still follows the selection.
 */
export function updateUseSelectedLabel(form) {
    const checked = form.querySelectorAll?.('input[type="checkbox"]:checked')?.length ?? 0;
    const button = form.querySelector?.('[data-steam-review-use-selected]');

    if (button instanceof HTMLElement) {
        // Never `Use 0 selected games`: with nothing ticked this is not an offer, so it stops looking
        // like one and stops being pressable.
        button.disabled = checked === 0;
        if (button.dataset?.steamReviewStaticLabel === undefined) {
            button.textContent = checked === 0
                ? 'Use selected games'
                : `Use ${checked} selected game${checked === 1 ? '' : 's'}`;
        }
    }

    const count = form.querySelector?.('[data-steam-review-selected-count]');
    if (count instanceof HTMLElement) {
        count.textContent = `${checked} selected`;
    }

    const selectAll = form.querySelector?.('[data-steam-review-select-all]');
    if (selectAll instanceof HTMLElement) {
        // Whether the press takes the collection or only the part of it on screen has to be readable
        // before the press, not discovered after it.
        const total = Number(selectAll.dataset?.steamReviewTotal ?? 0);
        const shown = shownCandidateBoxes(form).length;
        selectAll.textContent = shown < total
            ? `Select all ${shown} shown`
            : `Select all ${total}`;
    }
}

/**
 * Every answered entry's title list, put back into its collapsed state.
 *
 * Run on load and after every swap rather than tracked, because the server's answer is the authority on
 * what an entry now holds: a card that comes back with three titles where six were is a card whose
 * toggle has to disappear, and recomputing is the only way that cannot drift.
 */
function collapseMatchedTitles(root) {
    for (const group of root.querySelectorAll?.('[data-steam-review-titles]') ?? []) {
        renderMatchedTitles(group, false);
    }
}

/**
 * One title list at one state, and the toggle that describes it.
 *
 * The number is the titles actually hidden right now, never a stored one - and at or below the preview
 * there is nothing to reveal, so the control does not render at all rather than offering an expansion
 * of nothing.
 */
function renderMatchedTitles(group, expanded) {
    const items = group.querySelectorAll?.('[data-steam-review-title-item]') ?? [];
    const toggle = group.querySelector?.('[data-steam-review-titles-toggle]');
    if (!(toggle instanceof HTMLElement)) {
        return;
    }

    const hidden = items.length - MATCHED_TITLE_PREVIEW;
    if (hidden <= 0) {
        toggle.hidden = true;

        return;
    }

    let index = 0;
    for (const item of items) {
        item.hidden = !expanded && index >= MATCHED_TITLE_PREVIEW;
        index += 1;
    }

    toggle.hidden = false;
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    toggle.textContent = expanded ? 'Show less' : `Show ${hidden} more`;
}

/**
 * The checkboxes of the stored candidate list that the filter is not hiding.
 *
 * The stored list only: the games added through the catalogue search sit in their own list under their
 * own heading, and `Select all 58` is about the collection's contents rather than about everything on
 * the card.
 */
function shownCandidateBoxes(form) {
    const boxes = [];

    for (const candidate of form.querySelectorAll?.('[data-steam-review-candidate]') ?? []) {
        const box = candidate.querySelector?.('input[type="checkbox"]');
        if (candidate.hidden !== true && box !== null && box !== undefined) {
            boxes.push(box);
        }
    }

    return boxes;
}

function isModifiedClick(event) {
    return event?.defaultPrevented === true
        || event?.metaKey === true
        || event?.ctrlKey === true
        || event?.shiftKey === true
        || event?.altKey === true
        || (typeof event?.button === 'number' && event.button > 0);
}

function applyCandidateFilter(list, query) {
    const needle = String(query ?? '').trim().toLowerCase();
    const candidates = list.querySelectorAll?.('[data-steam-review-candidate]') ?? [];

    for (const candidate of candidates) {
        if (needle === '') {
            candidate.hidden = false;
            continue;
        }

        const title = candidate
            .querySelector?.('[data-steam-review-candidate-title]')
            ?.textContent ?? '';
        candidate.hidden = !title.toLowerCase().includes(needle);
    }
}

initSteamImportReview();
